import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import s from './Icon.module.css';
import '@repo/ui/Icon/sprite.svg?inject-body';
import { type IconNames } from '@repo/ui/Icon/utils';

export const iconSizeOptions = ['xs', 'sm', 'md', 'lg', 'xl', 'font'] as const;
export type IconSize = (typeof iconSizeOptions)[number];

export interface IconProps extends ComponentPropsWithRef<'span'> {
  className?: string;
  name: IconNames;
  size?: IconSize;
}

export const Icon = (props: IconProps) => {
  const { className, name, size = 'md', ...attrs } = props;

  // `role="img"` with no name is noise, so an unnamed icon defaults to hidden
  // and a named one defaults to exposed. Deriving the default is what makes
  // naming an icon enough to reach assistive tech; the pair that left several
  // tooltip triggers unreachable — a name plus `aria-hidden` — is still
  // reachable by passing `aria-hidden` explicitly, since `attrs` is spread last.
  const isNamed = Boolean(attrs['aria-label'] ?? attrs['aria-labelledby']);

  return (
    // A <span> root keeps Icon valid inside phrasing content (e.g. inline in
    // a <p>); layout is unaffected since the CSS sets inline-flex.
    <span
      className={clsx(s.icon, s[`${name}Icon`], s[`${size}Size`], className)}
      data-icon={name}
      role="img"
      aria-hidden={isNamed ? undefined : 'true'}
      {...attrs}
    >
      <svg
        width="100%"
        height="100%"
        role="presentation"
        focusable="false"
        className={s.svg}
      >
        <use xlinkHref={`#n-icon-${name}`} />
      </svg>
    </span>
  );
};
